import type { Permission } from '@alicerce/contracts';

/** Passo como a API devolve (`GET /v1/core/dashboard/setup`); cada módulo ativo contribui os seus. */
export interface ApiSetupStep {
  key: string;
  moduleKey: string;
  label: string;
  done: boolean;
  link?: string;
  waiting?: boolean;
}

/** Texto do guia para um passo: o que é, por que importa, como fazer e a comemoração ao concluir. */
export interface StepGuide {
  title: string;
  why: string;
  howTo: string[];
  /** Quem não tem esta permissão não vê o passo (ex.: administrador não mexe na conta de recebimento). */
  permission?: Permission;
  /** Só fica disponível depois destes passos (ex.: saque só depois da conta aprovada). */
  requires?: string[];
  /** Mostrado enquanto o passo espera alguém de fora (análise do meio de pagamento). */
  waitingText?: string;
  celebrate: { title: string; message: string };
}

/** Catálogo na ordem em que sugerimos configurar. Um passo novo da API sem entrada aqui vai para o fim. */
export const STEP_GUIDES: [string, StepGuide][] = [
  [
    'core.business_data',
    {
      title: 'Dados do negócio',
      why: 'Seu nome, CPF ou CNPJ e regime tributário aparecem nos recibos, nas notas e na loja.',
      howTo: [
        'Confira o nome que seus clientes vão ver.',
        'Informe seu CPF ou CNPJ, só os números.',
        'Escolha o regime tributário. Se não souber, pergunte ao seu contador.',
        'Clique em Salvar no fim da página.',
      ],
      permission: 'tenant:update',
      celebrate: { title: 'Negócio cadastrado!', message: 'Seus dados já aparecem certinhos nos recibos, nas notas e na sua loja.' },
    },
  ],
  [
    'core.logo',
    {
      title: 'Logo da sua marca',
      why: 'O logo aparece na loja, nos e-mails e nos recibos, e deixa tudo com a cara do seu negócio.',
      howTo: ['Na parte do logo, clique para enviar a imagem (PNG ou JPG).', 'Prefira uma imagem quadrada, de preferência com fundo transparente.', 'Salve as alterações.'],
      permission: 'tenant:update',
      celebrate: { title: 'Sua marca está no ar!', message: 'O logo já aparece na loja, nos e-mails e nos recibos.' },
    },
  ],
  [
    'core.first_product',
    {
      title: 'Primeiro produto',
      why: 'Com um produto cadastrado você já pode vender pela loja, pela venda rápida ou mandando uma cobrança.',
      howTo: [
        'Dê um nome claro, do jeito que o cliente procuraria.',
        'Informe o preço. Se tiver tamanhos ou cores, adicione as variações.',
        'Envie pelo menos uma foto: produto com foto vende mais.',
        'Clique em Salvar.',
      ],
      permission: 'catalog:write',
      celebrate: { title: 'Primeiro produto cadastrado!', message: 'Ele já pode ser vendido. Tem muitos produtos? Dá para importar uma planilha em Produtos.' },
    },
  ],
  [
    'checkout.payment_account',
    {
      title: 'Conta de recebimento',
      why: 'É por ela que entram os pagamentos das suas vendas: Pix, cartão e boleto.',
      howTo: [
        'Preencha seus dados e o endereço do negócio.',
        'Envie os documentos pedidos aqui mesmo, sem sair da Gestora.',
        'Depois é só aguardar a análise do meio de pagamento. A gente avisa quando aprovar.',
      ],
      permission: 'checkout:payment-account',
      waitingText: 'Recebemos seu cadastro! O meio de pagamento está analisando seus dados. Enquanto isso, você pode seguir com os outros passos.',
      celebrate: { title: 'Conta de recebimento aprovada!', message: 'Agora sua loja aceita Pix, cartão e boleto, e o dinheiro das vendas cai na sua conta de recebimento.' },
    },
  ],
  [
    'checkout.payout_destination',
    {
      title: 'Carteira para seus saques',
      why: 'É para essa chave Pix ou conta bancária que o dinheiro das vendas vai quando você sacar.',
      howTo: [
        'Na parte de Saques, escolha receber por chave Pix ou por conta bancária.',
        'Use uma conta no seu nome ou no CNPJ do negócio.',
        'Escolha se prefere sacar quando quiser, pelo botão, ou em datas automáticas.',
        'Salve.',
      ],
      permission: 'checkout:payment-account',
      requires: ['checkout.payment_account'],
      celebrate: {
        title: 'Carteira de saque cadastrada!',
        message: 'Agora você pode sacar tudo o que receber na sua loja direto para a sua conta, sem sair da Gestora. A taxa aparece antes de cada saque.',
      },
    },
  ],
  [
    'checkout.shipping',
    {
      title: 'Entrega e retirada',
      why: 'Defina como as compras chegam ao cliente: por transportadora ou retirada no local.',
      howTo: [
        'Para entregar, conecte sua conta do Melhor Envio. O frete é calculado sozinho no carrinho.',
        'Se o cliente pode buscar, ligue a retirada no local e informe o endereço.',
        'Salve.',
      ],
      permission: 'orders:write',
      celebrate: { title: 'Entrega configurada!', message: 'Seus clientes já veem as opções de frete e de retirada na hora de comprar.' },
    },
  ],
  [
    'storefront.publish',
    {
      title: 'Loja no ar',
      why: 'Publicar coloca sua loja na internet, com um endereço para você divulgar.',
      howTo: ['Escolha um modelo de vitrine e ajuste cores e textos.', 'Use a prévia para ver como fica no celular e no computador.', 'Quando gostar, clique em Publicar.'],
      permission: 'storefront:manage',
      celebrate: { title: 'Sua loja está no ar!', message: 'Já dá para compartilhar o link da loja com seus clientes no Instagram e no WhatsApp.' },
    },
  ],
  [
    'agenda.open',
    {
      title: 'Agenda online',
      why: 'Com a agenda aberta, seus clientes marcam horário sozinhos pela loja, sem troca de mensagens.',
      howTo: ['Defina os dias e horários em que você atende.', 'Cadastre os serviços com duração e preço.', 'Ligue o agendamento online e salve.'],
      permission: 'bookings:write',
      celebrate: { title: 'Agenda aberta!', message: 'Seus clientes já podem marcar horário pela loja, e você recebe um aviso a cada novo agendamento.' },
    },
  ],
  [
    'inventory.initial_stock',
    {
      title: 'Estoque inicial',
      why: 'Informar quanto você tem de cada produto evita vender o que acabou e avisa quando algo estiver no fim.',
      howTo: ['Escolha o produto e informe a quantidade que você tem hoje.', 'Repita para os outros produtos.', 'Confirme a entrada.'],
      permission: 'inventory:adjust',
      celebrate: { title: 'Estoque cadastrado!', message: 'A partir de agora cada venda baixa o estoque sozinha, e avisamos quando algo estiver acabando.' },
    },
  ],
  [
    'finance.recurring',
    {
      title: 'Despesas fixas',
      why: 'Aluguel, internet, DAS do MEI: cadastrando uma vez, elas entram todo mês e você enxerga o lucro de verdade.',
      howTo: ['Clique para adicionar uma despesa fixa.', 'Informe o nome, o valor e o dia em que vence.', 'Repita para as principais contas do mês.'],
      permission: 'finance:write',
      celebrate: { title: 'Despesas fixas cadastradas!', message: 'Agora o Gastos e lucros mostra quanto realmente sobra no fim do mês.' },
    },
  ],
  [
    'fiscal.company',
    {
      title: 'Dados fiscais',
      why: 'São os dados que vão impressos em cada nota fiscal.',
      howTo: ['Confira o CNPJ, a inscrição estadual ou municipal e o endereço.', 'Salve.'],
      permission: 'fiscal:write',
      celebrate: { title: 'Dados fiscais prontos!', message: 'Falta pouco para você emitir suas notas por aqui.' },
    },
  ],
  [
    'fiscal.certificate',
    {
      title: 'Certificado digital',
      why: 'O certificado A1 é a assinatura do seu negócio nas notas fiscais.',
      howTo: ['Envie o arquivo do certificado A1 (.pfx) e digite a senha dele.', 'Ele fica guardado com criptografia.'],
      permission: 'fiscal:certificate',
      celebrate: { title: 'Certificado enviado!', message: 'Suas notas já podem ser assinadas pela Gestora.' },
    },
  ],
  [
    'fiscal.profiles',
    {
      title: 'Perfis tributários',
      why: 'Eles dizem quais impostos vão em cada tipo de produto ou serviço.',
      howTo: ['Crie um perfil para cada tipo de produto ou serviço.', 'Confirme os dados com seu contador.'],
      permission: 'fiscal:write',
      celebrate: { title: 'Perfis criados!', message: 'Cada venda já sai com os impostos certos na nota.' },
    },
  ],
  [
    'fiscal.homologation',
    {
      title: 'Nota de teste',
      why: 'Uma nota de teste confere se está tudo certo antes da primeira nota de verdade.',
      howTo: ['Emita uma nota de teste. Ela não vale como nota real.', 'Se der certo, a emissão de verdade é liberada.'],
      permission: 'fiscal:write',
      requires: ['fiscal.company', 'fiscal.certificate', 'fiscal.profiles'],
      celebrate: { title: 'Notas fiscais liberadas!', message: 'Agora você pode emitir notas de verdade para as suas vendas.' },
    },
  ],
  [
    'inbox.whatsapp',
    {
      title: 'WhatsApp do negócio',
      why: 'Responda os clientes do WhatsApp aqui no painel, com toda a equipe junto.',
      howTo: ['Clique em conectar e entre com a conta do Facebook do negócio.', 'Escolha o número do WhatsApp Business.', 'Pronto: as conversas aparecem em Atendimento.'],
      permission: 'inbox:channels',
      celebrate: { title: 'WhatsApp conectado!', message: 'As mensagens dos clientes agora chegam no Atendimento, e sua equipe responde daqui mesmo.' },
    },
  ],
  [
    'ads.connect',
    {
      title: 'Anúncios',
      why: 'Conecte a página do Facebook para anunciar no Facebook e no Instagram sem sair da Gestora.',
      howTo: ['Clique em conectar e entre com a conta do Facebook do negócio.', 'Escolha a página e a conta de anúncios.', 'Confirme.'],
      permission: 'ads:write',
      celebrate: { title: 'Página conectada!', message: 'Você já pode criar anúncios e ver quanto cada um vendeu.' },
    },
  ],
  [
    'core.team',
    {
      title: 'Sua equipe',
      why: 'Convide quem trabalha com você. Cada pessoa só vê o que precisa para o trabalho dela.',
      howTo: ['Clique em convidar e informe o e-mail da pessoa.', 'Escolha o papel: vendas, financeiro, atendimento ou administração.', 'A pessoa recebe o convite por e-mail.'],
      permission: 'team:manage',
      celebrate: { title: 'Equipe convidada!', message: 'Assim que aceitarem o convite, já podem ajudar no dia a dia.' },
    },
  ],
];

const GUIDES = new Map(STEP_GUIDES);
const ORDER = new Map(STEP_GUIDES.map(([key], i) => [key, i]));

export interface SetupStep extends ApiSetupStep {
  guide: StepGuide;
  /** Depende de um passo que ainda não foi feito. */
  locked: boolean;
  skipped: boolean;
}

export interface SetupState {
  steps: SetupStep[];
  done: number;
  total: number;
  /** Sugestão do botão "Próximo passo"; `null` quando não sobra nada que a pessoa possa fazer agora. */
  next: SetupStep | null;
  complete: boolean;
}

export function guideFor(step: ApiSetupStep): StepGuide {
  return GUIDES.get(step.key) ?? { title: step.label, why: '', howTo: [], celebrate: { title: 'Passo concluído!', message: step.label } };
}

/**
 * Junta os passos da API com o catálogo do guia: tira os que a pessoa não pode fazer, ordena na
 * sequência sugerida e escolhe o próximo (passos pulados vão para o fim da fila).
 */
export function buildSetup(api: ApiSetupStep[], can: (p: Permission) => boolean, skipped: string[] = []): SetupState {
  const doneKeys = new Set(api.filter((s) => s.done).map((s) => s.key));
  const skippedSet = new Set(skipped);
  const steps = api
    .map((s) => ({ s, guide: guideFor(s) }))
    .filter(({ guide }) => !guide.permission || can(guide.permission))
    .sort((a, b) => (ORDER.get(a.s.key) ?? Infinity) - (ORDER.get(b.s.key) ?? Infinity))
    .map<SetupStep>(({ s, guide }) => ({
      ...s,
      waiting: !s.done && !!s.waiting,
      guide,
      locked: !s.done && (guide.requires ?? []).some((k) => api.some((o) => o.key === k) && !doneKeys.has(k)),
      skipped: skippedSet.has(s.key),
    }));
  const available = steps.filter((s) => !s.done && !s.waiting && !s.locked);
  const next = available.find((s) => !s.skipped) ?? available[0] ?? null;
  const done = steps.filter((s) => s.done).length;
  return { steps, done, total: steps.length, next, complete: steps.length > 0 && done === steps.length };
}

/** Passos que ficaram prontos desde a última visita e ainda não ganharam os parabéns. */
export function newlyDone(celebrated: string[], steps: SetupStep[]): SetupStep[] {
  const seen = new Set(celebrated);
  return steps.filter((s) => s.done && !seen.has(s.key));
}

/** A tela atual é a do passo (o link pode ter query, e telas filhas contam). */
export function isOnStepPage(pathname: string, step: Pick<ApiSetupStep, 'link'>): boolean {
  if (!step.link || step.link.startsWith('http')) return false;
  const path = step.link.split('?')[0]!.replace(/\/$/, '') || '/';
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}
